import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";

import { fleet, type CandidateAsset, type CommandSelector } from "@/api/client";
import { Field } from "@/components/Field";
import { Select, SearchInput } from "@/components/inputs";
import { FilePick } from "@/components/FilePick";
import { Icon } from "@/components/icons";
//: LAZY: Leaflet is ~145 KB and creating an immobilise run must not
//: pay for it. Same arrangement as RouteReplay.
const FenceMap = lazy(() => import("@/modules/fleet/FenceMap")
  .then((m) => ({ default: m.FenceMap })));
import { assetData } from "@/api/client";
import { CAP, useSession } from "@/auth/session";
import { useCompanyScope } from "@/auth/companyScope";
import { COMMANDS, actionClass, toneVar } from "@/modules/fleet/catalogue";

/** New run. FOTA's "New rollout" box, with a command selector on top.
 *
 *  MIRRORED ON PURPOSE (Dattatray: *"Make surr we mirror fota UI as much as
 *  possible with and addtion of commadn selection"*). Same numbered fields,
 *  same "N shown · M selected" line, same tick-box table with a select-all in
 *  the header, same name field and count-labelled create button. An operator
 *  who has rolled firmware already knows this screen.
 *
 *  TWO WAYS TO NAME THE TARGETS, ONE AT A TIME. FOTA has only the filter; a
 *  command run also needs the pasted/CSV list Dattatray asked for. They are a
 *  SEGMENTED CHOICE rather than two live panels, because two populated target
 *  lists mean the button's blast radius depends on which you touched last —
 *  the one-element-two-forms bug this console has shipped before.
 *
 *  The server takes either and runs the same code afterwards, so a ticked run
 *  and a filtered run cannot diverge in what they check.
 */

type Mode = "list" | "bulk";

/** One asset's vehicle-control state, coloured as Dattatray asked:
 *  green mobilised, red immobilised (naming which kind), amber for a
 *  restraint that is commanded but NOT yet applied.
 *
 *  THE AMBER STATE IS THE WHOLE POINT. "Sent but not applied" means the
 *  vehicle is still drivable -- it is waiting for a halt, or for key-off, or
 *  the command never arrived. Painting it red would tell an operator a
 *  vehicle is stopped when it is not, which is the same lie the Live Window's
 *  headline was rewritten to remove.
 */
function VehicleCell({ a }: { a: CandidateAsset }) {
  const kind = a.control_action === 3 ? "wheel lock"
    : a.control_action === 2 ? "throttle" : null;
  if (a.control === "commanded") {
    return (
      <span style={{ color: "var(--warn)", fontWeight: 700, fontSize: 12 }}
        title="Orbit has published this restraint and the vehicle has not applied it yet. It can still be driven until it stops.">
        Immobilising{kind ? ` (${kind})` : ""}
      </span>
    );
  }
  if (a.control === "held") {
    return (
      <span style={{ color: "var(--bad)", fontWeight: 700, fontSize: 12 }}
        title="The vehicle reports it is enforcing this restraint.">
        Immobilised{kind ? ` (${kind})` : ""}
      </span>
    );
  }
  if (a.control === "mobile") {
    return (
      <span style={{ color: "var(--ok)", fontWeight: 700, fontSize: 12 }}
        title="The vehicle reports no restraint in force.">Mobilised</span>
    );
  }
  //: NOT GREEN. A vehicle that has never reported a control word is not known
  //: to be free, and the muted tone is the honest answer -- the same reason
  //: the held-by grid shows an absent cause as a dash rather than a blank.
  return <span className="cap" title="No control word reported yet.">—</span>;
}

/** The countdown to when this asset can be immobilised again.
 *
 *  Dattatray: *"Countdown timer per device after clearing the geo fence to
 *  accept immobilise again"*. While a recovery window is live NOTHING stops
 *  the vehicle at that level -- not the fence, not a run, not an explicit
 *  command -- so an operator looking at this table needs to know the refusal
 *  is coming before they press the button.
 *
 *  TICKS IN THE BROWSER off an absolute server timestamp, so clock skew moves
 *  the displayed number rather than the deadline, and a paused tab catches up
 *  rather than drifting.
 */
function LockoutCell({ until }: { until: string | null }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!until) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [until]);
  if (!until) return <span className="cap">—</span>;
  const left = Math.max(0, new Date(until).getTime() - now);
  if (left === 0) return <span className="cap">—</span>;
  const m = Math.floor(left / 60000), sec = Math.floor((left % 60000) / 1000);
  return (
    <span style={{ color: "var(--warn)", fontWeight: 700, fontSize: 12,
                   fontVariantNumeric: "tabular-nums" }}
      title={`No immobilise will be accepted at this level until ${new Date(until).toLocaleString()}.`}>
      {m > 59 ? `${Math.floor(m / 60)}h ${m % 60}m`
        : m > 0 ? `${m}m ${String(sec).padStart(2, "0")}s`
          : `${sec}s`}
    </span>
  );
}


export function NewRun({ onCreated, focus }: {
  onCreated: (batchId: string) => void;
  /** ONE ASSET, arrived at from the map or a deep link. The run builder opens
   *  scoped to it and with it already ticked, so "Create run" targets exactly
   *  the vehicle the operator clicked.
   *
   *  Dattatray: *"the link from asset data page shall open only that asset in
   *  New Run Section"*. The alternative was a second command surface for one
   *  asset, which is how two command pickers appeared on this page once
   *  already - and two places that stop vehicles drift apart. A single asset
   *  is a run of one. */
  focus?: { assetId: string; company: string } | null;
}) {
  // THE WRITE IS GATED HERE TOO, not only on the server. `require_cap`
  // refuses the call either way, but a button that looks live and returns 403
  // teaches an operator nothing — the same nav-says-yes-server-says-no gap
  // that moved this module out of Asset Data (D-111).
  const { can } = useSession();
  //: `cross` IS GONE WITH THE REASON SELECTOR (D-133). It mirrored the
  //: server's `is_internal(role)` and existed for exactly one purpose:
  //: deciding whether to offer the reason codes. Nothing else on this screen
  //: varies by whether the caller belongs to a company -- the level comes
  //: from the role server-side and was never a control here.
  const mayCommand = can(CAP.IMMOBILIZE);
  const [cmdKey, setCmdKey] = useState("immobilize");
  const spec = COMMANDS.find((c) => c.key === cmdKey) ?? COMMANDS[0];
  const [mode, setMode] = useState<Mode>("list");

  //: THE COMPANY COMES FROM THE APP BAR (D-134), not from a control here. A
  //: focused visit (arrived from the map with one asset) carries its own.
  const scope = useCompanyScope();
  const company = focus ? focus.company : scope.company;
  const [assetType, setAssetType] = useState("");
  const [state, setState] = useState("");
  const [q, setQ] = useState("");
  const [action, setAction] = useState<number>(spec.options[0]?.action ?? 1);
  // THE 2-BIT WIRE REASON, and `null` is not 0. null omits `rsn` from the
  // command entirely; 0 means the operator chose `unspecified`, which the
  // device stores and reports as a value. The server keeps the same
  // distinction, so the default has to be null rather than 0.
  //: 0 (`unspecified`), never null: there is no "send no reason" any more,
  //: because the firmware's own default for an absent `rsn` IS 0.
  //: GEOFENCE FIELDS. Only rendered for that command, and deliberately kept
  //: in this component rather than a child: they are command OPTIONS, like
  //: the immobilise action, and the targets above them are shared.
  //: "circle" sets one, "disable" turns this level's off. Both use the
  //: target list above, which is the point: turning a fence off should not
  //: need a different screen and a different mental model from setting one.
  const [fenceKind, setFenceKind] = useState<"circle" | "disable">("circle");
  //: WHAT THE SERVER ACTUALLY DID, kept and shown. Both fence mutations used
  //: to discard the response and clear the form, so "disabled 0 of 1" looked
  //: exactly like success -- which is how a disable that matched no row at
  //: all went unnoticed until a vehicle stayed immobilised by a fence the
  //: operator had switched off. A partial outcome has to be visible.
  const [fenceOut, setFenceOut] = useState<string | null>(null);
  const [fenceName, setFenceName] = useState("");
  const [fLat, setFLat] = useState("");
  const [fLng, setFLng] = useState("");
  const [fKm, setFKm] = useState("0.3");
  //: NO CENTRE-MODE PICKER. *"each aseset own home: This drop option is
  //: useless: we already have lust to select assts from."* -- right: the
  //: target list above already says which assets, so one circle drawn for
  //: that selection is the whole statement. The server still supports a
  //: per-home fence and nothing in the console creates one.
  //:
  //: NO "ON BREACH" PICKER EITHER. *"there is nothing to warn on device
  //: level right? So lets have implementation for Dierctly immbilize"* --
  //: correct, and it was my own finding: the cluster shows a reason only
  //: once a hold is APPLIED, which is too late to be a warning. Offering
  //: "warn, then immobilise" promised a rider-facing notice that does not
  //: exist. A fence immobilises directly until there is a wire path for a
  //: driver message.
  const [name, setName] = useState("");
  const [wave, setWave] = useState("");
  //: "" = let the server pick the default for this action, so the console
  //: never restates 24h/15min as its own constant and drifts from it
  const [lifeMin, setLifeMin] = useState("");
  //: THE RECOVERY WINDOW, in minutes, on a mobilise. Dattatray: *"Once the
  //: immbolise command for asset is out while the asset is out of geo fense :
  //: the next immobilse comamnd can be send after a timout only (maybe
  //: configurable while sending mobolise command)"*.
  //:
  //: WITHOUT IT THE FENCE DEADLOCKS A RECOVERY. The vehicle is outside its
  //: boundary, so releasing it to drive home gets it stopped again on the
  //: next tick -- the rider gets about two minutes, repeatedly, and the
  //: vehicle can never be brought back. The window is the operator saying
  //: "I know it is outside. I am bringing it in. Leave it alone."
  const [lockMin, setLockMin] = useState("");
  const [picked, setPicked] = useState<string[]>([]);
  const [text, setText] = useState("");
  const [fileName, setFileName] = useState("");
  const [parseError, setParseError] = useState("");

  // WRAPPED, not passed by reference: `assetTypes(company?)` takes an
  // argument, and react-query calls its queryFn with a CONTEXT object — which
  // would arrive as the company filter.
  const types = useQuery({
    queryKey: ["fleet", "asset-types", company],
    queryFn: () => assetData.assetTypes(company || undefined) });

  // A new scope means a new candidate list: nothing ticked carries over.
  useEffect(() => { setPicked([]); }, [scope.company]);

  // ADOPT THE FOCUSED ASSET: its company answers the Owner question, and its
  // id goes in the search, so the list resolves to exactly that one row.
  useEffect(() => {
    if (!focus) return;
    setQ(focus.assetId);
  }, [focus?.assetId, focus?.company]);

  const selector: CommandSelector = useMemo(() => ({
    ...(company ? { company } : {}),
    ...(assetType ? { asset_type: assetType } : {}),
    ...(state ? { state } : {}),
    ...(q.trim() ? { q: q.trim() } : {}),
  }), [company, assetType, state, q]);

  // THE SAME FILTER the run will use. FOTA's `_candidate_conds` exists so the
  // picker, the count and the batch cannot disagree; this is the client half
  // of that — what is listed here is what the server resolves.
  // A COMPANY IS REQUIRED BEFORE ANYTHING IS LISTED. Without it the page
  // would open showing every asset Orbit can reach, pre-selected for a
  // command that stops vehicles.
  const cands = useQuery({
    queryKey: ["fleet", "candidates", selector],
    queryFn: () => fleet.candidates(selector),
    // NOT WHEN FOCUSED. The picker is not rendered at all in that mode, so
    // this would be a round trip whose result nothing displays -- and the
    // query still became ENABLED, because adopting the focused asset sets
    // `company`. Caught by a test asserting no candidate call, which is the
    // only way an invisible request shows up.
    enabled: mode === "list" && company !== "" && !focus,
  });
  const shown: CandidateAsset[] = cands.data?.assets ?? [];
  const total = cands.data?.total ?? 0;

  // ...and tick it ONCE, when the asset changes - not every time the list
  // does.
  //
  // THIS WAS A REAL BUG AND IT IS WORTH NAMING. The dependency used to include
  // `shown`, and the body REPLACED the whole selection
  // (`setPicked([focus.assetId])`). So every refresh of the candidate list
  // threw away whatever the operator had ticked: add two assets, type one
  // character in the search, and the selection collapsed back to one. Unticking
  // the focused asset was impossible too - the next list refresh put it back.
  // `focus` never clears, so it lasted the whole visit.
  //
  // Keyed on the ASSET now, so arriving at a vehicle selects it and everything
  // after that belongs to the operator.
  useEffect(() => {
    if (!focus) return;
    setPicked([focus.assetId]);
  }, [focus?.assetId]);

  // ONE ROW PER LINE, `asset_id` or `asset_id,reason`. Dattatray asked for a
  // reason column in the template and the upload, so the second cell now
  // MEANS something and the list can no longer be split on commas as well as
  // whitespace: `AE-1,2` has to read as one vehicle with reason 2, not as two
  // vehicles called "AE-1" and "2".
  //
  // THAT DROPS AN UNDOCUMENTED CONVENIENCE -- a comma-separated list on one
  // line used to work, and the label has always said "one asset id per row".
  // The trade is deliberate and the failure is SAFE: such a line now reports
  // "2 is not a reason code" instead of silently commanding a vehicle that
  // does not exist. Silently reading cell 2 as an id while the sheet means it
  // as a reason is the ambiguity worth paying to avoid.
  //
  // A CODE A NON-PLATFORM OPERATOR CANNOT SEND IS REPORTED, NOT DROPPED. They
  // cannot download a sheet carrying the column, but they can be handed one,
  // and `_reason` answers 403 for any value -- so dropping it quietly would
  // mean uploading 500 rows and being told nothing about why the run refused.
  //: ONE COLUMN AGAIN (D-133). The second column was a reason code, added
  //: 2026-10-24 and deleted 2026-10-25: its 24 `(level, code)` meanings were
  //: never defined, and `place_hold` dropped the value on a re-aim anyway, so
  //: Orbit recorded one code while the device reported another for the same
  //: hold. Dattatray: *"Simple 4 levbel scheme with no reason field"*.
  //:
  //: EXTRA CELLS ARE STILL REPORTED RATHER THAN IGNORED. A sheet with a
  //: second column is one an operator built against the old template, and
  //: silently reading only its first cell is the substitution failure this
  //: parser was written to avoid -- `AE-A,2` must not become a vehicle called
  //: "AE-A" whose 2 was quietly discarded, because the operator believes the
  //: 2 did something.
  const parsed = useMemo(() => {
    const ids: string[] = [];
    const bad: string[] = [];
    for (const raw of text.split(/\r?\n/)) {
      const line = raw.trim();
      if (!line || line.startsWith("#")) continue;
      const cells = line.split(",")
        .map((c) => c.trim().replace(/^"(.*)"$/, "$1").trim());
      const id = cells[0];
      if (!id || id.toLowerCase() === "asset_id") continue;
      if (cells.slice(1).some((c) => c !== "")) { bad.push(line); continue; }
      ids.push(id);
    }
    return { ids, bad, ignored: 0 };
  }, [text]);
  const pastedIds = parsed.ids;
  const targetCount = mode === "list" ? picked.length : pastedIds.length;
  const isFence = spec.key === "geofence";
  const fLatN = Number(fLat), fLngN = Number(fLng), fKmN = Number(fKm);
  //: A per-home fence needs NO centre -- each asset brings its own -- so the
  //: coordinate boxes are not required in that mode. Validating them anyway
  //: would make the common fleet rule impossible to submit.
  const fenceReady = isFence && (fenceKind === "disable"
    || (Number.isFinite(fKmN) && fKmN >= 0.05 && fKmN <= 200
        && fLat !== "" && fLng !== "" && Number.isFinite(fLatN)
        && Number.isFinite(fLngN)));

  const disableFence = useMutation({
    mutationFn: () => fleet.disableGeofences(
      mode === "list" ? picked : pastedIds),
    onSuccess: (r) => {
      const none = r.had_none?.length ?? 0;
      //: A FENCE GOES OFF WITHOUT MOBILISING when something else has claimed
      //: that level -- a theft report takes the hold over, and then only a
      //: person can lift it. Silence here would read as "mobilised", which
      //: is the opposite of what happened.
      const stuck = r.disabled - (r.released ?? 0);
      setFenceOut(
        `${r.disabled} fence${r.disabled === 1 ? "" : "s"} off`
        + (r.released ? `, ${r.released} mobilised` : "")
        + (stuck > 0
            ? `, ${stuck} still held at your level by another cause` : "")
        //: NAMED, not just counted: "2 had no fence at your level" sends an
        //: operator to look, where a bare "2" reads as a rounding error.
        + (none ? `. ${none} had no fence at your level (${
            r.had_none.slice(0, 3).join(", ")}${none > 3 ? ", …" : ""})` : ""));
      //: THE SELECTION SURVIVES A PARTIAL RESULT. Clearing it would throw
      //: away the list the operator now needs in order to retry.
      if (!none) { setPicked([]); setText(""); }
      onCreated("");
    },
  });

  const createFence = useMutation({
    mutationFn: () => fleet.bulkGeofence({
      asset_ids: mode === "list" ? picked : pastedIds,
      name: fenceName.trim() || "fence",
      centre_mode: "absolute",
      centre_lat: fLatN, centre_lng: fLngN,
      radius_m: Math.round(fKmN * 1000),
      action,
      //: DIRECT IMMOBILISE. There is no device-level warning to give, so a
      //: grace period would be a delay with nothing happening in it.
      warn_first: false,
      grace_s: 0,
    }),
    //: `onCreated` is the page's own refresh hook, already used by the run
    //: path. Reusing it keeps this component free of a query client it
    //: otherwise has no need for.
    onSuccess: (r) => {
      const skip = r.skipped?.length ?? 0;
      setFenceOut(`${r.created ?? 0} fence${r.created === 1 ? "" : "s"} armed`
        + (skip ? `. ${skip} refused (${r.skipped.slice(0, 3).map((k) => k.asset_id).join(", ")}${
            skip > 3 ? ", …" : ""})` : ""));
      if (!skip) { setPicked([]); setText(""); setFenceName(""); }
      onCreated("");
    },
  });

  const create = useMutation({
    mutationFn: () => fleet.createBatch({
      ...selector,
      ...(mode === "list" ? { asset_ids: picked } : { asset_ids: pastedIds }),
      name: name.trim() || undefined,
      lifetime_minutes: lifeMin ? Number(lifeMin) : undefined,
      //: MOBILISE ONLY, and the server refuses it on a restraint (400) as
      //: well -- a window that began by stopping a vehicle would lock the
      //: operator out of undoing their own command.
      ...(spec.key === "mobilize" && lockMin
          ? { lockout_minutes: Number(lockMin) } : {}),
      action: spec.key === "mobilize" ? 1 : action,
      // ALWAYS SENT ON AN IMMOBILISE BY A PLATFORM OPERATOR, and the default
      // is 0 (`unspecified`). Dattatray: *"Not set and Upspecified are same
      // thing. We should haev default unsopecified and remove not set
      // option"* -- and he is right that my null/0 split was imaginary HERE.
      // The firmware defaults `rsn` to 0, so omitting the key and sending 0
      // reach the device identically. The distinction is real only on the ACK
      // side, where null means "this firmware does not report it".
      //
      // GUARDED ON `cross`, NOT JUST ON THE CONTROL BEING RENDERED. `_reason`
      // answers 403 to any value from a company admin, so a default of 0 sent
      // unconditionally would refuse every run they created.
      //
      wave_size: wave ? Number(wave) : undefined,
    }),
    onSuccess: (r) => { setPicked([]); setText(""); onCreated(r.batch_id); },
  });

  const onFile = async (f: File | null) => {
    setFileName(f?.name ?? "");
    setParseError("");
    if (!f) return;
    try {
      // BOM first: Excel writes one when it saves a CSV, so the header cell
      // arrives as "﻿asset_id" and would be uploaded as an asset id.
      const body = (await f.text()).replace(/^﻿/, "");
      // THE FIRST TWO CELLS, not just the first: the immobilise template now
      // carries `asset_id,reason`. Written back into the textarea as
      // `id,code` so WHAT THE OPERATOR SEES IS WHAT WILL BE SENT -- the
      // alternative, a hidden map beside a list of bare ids, is state the
      // screen does not show and the one thing the UI laws forbid outright.
      const rows = body.split(/\r?\n/)
        .map((l) => l.split(",").slice(0, 2)
          .map((c) => c.trim().replace(/^"(.*)"$/, "$1").trim()))
        .filter(([id]) => id && !id.startsWith("#")
                && id.toLowerCase() !== "asset_id")
        .map(([id, code]) => (code ? `${id},${code}` : id));
      if (rows.length === 0) {
        setParseError("that file has no asset rows in its first column");
        return;
      }
      setText(rows.join("\n"));
    } catch (e) {
      setParseError(e instanceof Error ? e.message : String(e));
    }
  };

  const allShownPicked = shown.length > 0
    && shown.every((a) => picked.includes(a.asset_id));

  return (
    <div className="panel panel-pad">
      {/* 1 · THE COMMAND - the addition to FOTA's box, and it comes first
          because every field under it depends on which command this is. */}
      <Field label="1 · Command">
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {COMMANDS.map((c) => (
            <button key={c.key} type="button" disabled={!c.built}
              onClick={() => { setCmdKey(c.key); setAction(c.options[0]?.action ?? 1); }}
              title={c.built ? c.hint : `${c.label} is not built yet`}
              style={{
                font: "inherit", fontSize: 12.5,
                fontWeight: c.key === cmdKey ? 700 : 500,
                padding: "7px 14px", borderRadius: 9,
                cursor: c.built ? "pointer" : "not-allowed",
                opacity: c.built ? 1 : 0.5,
                border: `1px solid ${c.key === cmdKey ? toneVar(c) : "var(--line)"}`,
                background: c.key === cmdKey
                  ? `color-mix(in srgb, ${toneVar(c)} 10%, transparent)`
                  : "transparent",
              }}>
              <Icon name={c.icon} /> {c.label}
            </button>
          ))}
        </div>
      </Field>

      {/* WHO IT APPLIES TO - fleet mode only. Arriving from the map the
          vehicle is already chosen, so an Owner / Asset type / Find / Lifecycle
          row is four controls whose only job is to choose the thing that is
          already chosen. Dattatray: *"we only show run when we nagivate from
          the asset data and make sure that that exact same asset is ready for
          a command"*. */}
      {focus ? (
        <div style={{ marginTop: 12 }}>
          <Field label="2 · Asset">
            <div className="mono" style={{ fontSize: 13, fontWeight: 700,
                  color: "var(--t-bright)", padding: "7px 0" }}>
              {focus.assetId}
              <span style={{ fontWeight: 400, fontSize: 11.5,
                             color: "var(--t-mute)", marginLeft: 8 }}>
                {focus.company}
              </span>
            </div>
          </Field>
        </div>
      ) : (
      <div style={{ display: "grid", gap: 12, marginTop: 12,
                    gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))" }}>
        {/* NO OWNER CONTROL HERE (D-134): the company is the app bar's.
            What survives is the rule it used to carry. Dattatray: *"the
            default selection shall not be All companies It should be no
            companies!"* -- "All companies" as the OPENING state means the
            widest possible blast radius is what you get by touching nothing,
            on the one page that stops vehicles. So with the bar at ALL this
            page lists nothing and says to narrow it; `scope.needsOne`. */}
        <Field label="2 · Asset type">
          <Select className="fill" value={assetType}
            onChange={(e) => { setAssetType(e.target.value); setPicked([]); }}>
            <option value="">Any</option>
            {/* `{asset_type, count}`, not a bare string - and showing the
                COUNT is the point: it tells you how big the filter is before
                you apply it, which is the same service the candidate list
                performs one step later. */}
            {(types.data?.asset_types ?? []).map((t) => (
              <option key={t.asset_type} value={t.asset_type}>
                {t.asset_type} ({t.count})
              </option>
            ))}
          </Select>
        </Field>
        {/* SEARCH, not just filters. The device picker had one and New run
            needed it: with thousands of assets, "the bike with this VIN" is
            the common question and no combination of company/type/state
            answers it. */}
        <Field label="3 · Find an asset">
          <SearchInput className="fill" placeholder="VIN / BIN / plate"
            value={q} onChange={(e) => { setQ(e.target.value); setPicked([]); }} />
        </Field>
        <Field label="4 · Lifecycle state">
          <Select className="fill" value={state}
            onChange={(e) => { setState(e.target.value); setPicked([]); }}>
            <option value="">Any state</option>
            <option value="deployed">deployed</option>
            <option value="inventory">inventory</option>
            <option value="repair">repair</option>
          </Select>
        </Field>
      </div>
      )}

      {/* THE LEVEL BELONGS TO BOTH MODES. Only for a command that HAS a choice
          - a release has no type - but a focused run still has to say WHICH
          immobilise, so it cannot live inside the fleet-only block. It was in
          the grid above, which is how it would have disappeared from the
          single-asset view and left the level silently defaulted. */}
      {spec.options.length > 0 && (
        <div style={{ marginTop: 12, display: "flex", gap: 12,
                      flexWrap: "wrap", alignItems: "flex-start" }}>
          <div style={{ flex: "1 1 240px", maxWidth: 330 }}>
          <Field label={`${focus ? 3 : 6} · ${spec.label} as`}>
            <Select className="fill" value={action}
              onChange={(e) => setAction(Number(e.target.value))}>
              {spec.options.map((o) => (
                <option key={o.action} value={o.action}>{o.label}</option>
              ))}
            </Select>
          </Field>
          </div>

          {/* THE REASON CONTROL IS GONE (D-133), and with it the only
              thing on this screen that asked an operator to choose a number
              whose meaning nobody had defined. A command is now: pick the
              assets, pick mobilise / freewheel / wheel lock, go.

              WHY a vehicle was stopped is recorded as `cause` on the hold --
              `geofence`, `operator`, later `theft` and `payment` -- written
              by the code path that places it rather than typed into a form,
              and shown as a word rather than a digit. */}
        </div>
      )}

      {/* ═══ GEOFENCE ═══ the command's own options, under the run
          builder's own target list. Dattatray: *"The geofense map should have
          opned under the new Run section itself when the geo fense command is
          selecetd"* -- so there is no second place to pick assets and no
          second place to draw.

          The ACTION (freewheel / wheel lock) and the REASON are the shared
          controls above: a fence is a command, and those are the same two
          questions every command asks. */}
      {isFence && (
        <>
          <div style={{ marginTop: 12, display: "grid",
                        gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))",
                        gap: 12 }}>
            {/* KEPT AT HIS REQUEST, with one option built. A picker showing
                what is coming and marking it unavailable is honest; the
                evaluation is identical for all three shapes, so only the
                geometry differs. */}
            <Field label={`${focus ? 5 : 8} · Fence type`}>
              <Select className="fill" value={fenceKind}
                onChange={(e) => setFenceKind(e.target.value as "circle" | "disable")}>
                <option value="circle">Circular geofence</option>
                <option value="pincode" disabled>Pincode area (not built)</option>
                <option value="polygon" disabled>Polygon (not built)</option>
                {/* THE WAY OFF, and it belongs here rather than on a
                    separate screen: the assets are already selected above,
                    and "no fence" is a choice about the same thing the other
                    options are choices about. */}
                <option value="disable">Disable geofence</option>
              </Select>
            </Field>
            {fenceKind === "circle" && (
            <>
            <Field label="Fence name">
              <SearchInput className="fill" placeholder="e.g. Rahatani depot"
                value={fenceName} onChange={(e) => setFenceName(e.target.value)} />
            </Field>
            <Field label="Centre latitude">
              <SearchInput className="fill" placeholder="18.59480"
                value={fLat} onChange={(e) => setFLat(e.target.value)} />
            </Field>
            <Field label="Longitude">
              <SearchInput className="fill" placeholder="73.78649"
                value={fLng} onChange={(e) => setFLng(e.target.value)} />
            </Field>
            <Field label="Radius (km)">
              <SearchInput className="fill" value={fKm}
                onChange={(e) => setFKm(e.target.value.replace(/[^\d.]/g, ""))} />
            </Field>
            </>
            )}
          </div>

          {/* 50 m FLOOR, AND IT IS NOT ARBITRARY: measured GPS error on a
              stationary board reaches 100 m at good HDOP, so a tighter fence
              would breach while parked -- the failure this feature exists to
              avoid. */}
          {fenceKind === "circle" && fKm !== "" && fKmN < 0.05 && (
            <div style={{ fontSize: 11.5, color: "var(--bad)", marginTop: 6 }}>
              Minimum 0.05 km (50 m): GPS error alone reaches 100 m on a
              stationary vehicle, so a tighter fence would breach while parked.
            </div>
          )}

          {fenceKind === "circle" && (
          <div style={{ marginTop: 10 }}>
            <Suspense fallback={<div className="panel panel-pad cap"
              style={{ height: "min(62vh, 560px)", minHeight: 360 }}>loading map…</div>}>
              <FenceMap lat={fLat === "" ? null : fLatN}
                lng={fLng === "" ? null : fLngN}
                radiusKm={Number.isFinite(fKmN) ? fKmN : 0.3}
                onMove={(a, b) => { setFLat(a.toFixed(5)); setFLng(b.toFixed(5)); }}
                //: THE GRIP FILLS THE SAME BOX. Dragging the edge and typing
                //: a radius are two ways at one value, so the number must
                //: land where the operator can read and correct it.
                onResize={(k) => setFKm(String(k))} />
            </Suspense>
          </div>
          )}

        </>
      )}

      {/* HOW THE TARGETS ARE NAMED. Exactly one is live - and NEITHER is,
          when the target is already decided. Arriving from the map there is
          nothing to choose from and nothing to paste: a list of one with its
          only row ticked, and a CSV box beside it, are both answers to a
          question the operator already answered by clicking a vehicle. */}
      {!focus && (<>
      <div style={{ display: "flex", gap: 8, margin: "14px 0 8px", flexWrap: "wrap" }}>
        {(["list", "bulk"] as Mode[]).map((m) => (
          <button key={m} type="button" onClick={() => setMode(m)}
            style={{
              font: "inherit", fontSize: 12.5, fontWeight: mode === m ? 700 : 500,
              padding: "6px 12px", borderRadius: 9, cursor: "pointer",
              border: `1px solid ${mode === m ? "var(--ok)" : "var(--line)"}`,
              background: mode === m
                ? "color-mix(in srgb, var(--ok) 10%, transparent)" : "transparent",
            }}>
            {m === "list" ? "Choose from list" : "Bulk: paste or CSV"}
          </button>
        ))}
      </div>

      {mode === "list" ? (
        <>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
            <span style={{ fontSize: 11.5, color: "var(--t-mute)" }}>
              {shown.length} shown{total > shown.length ? ` of ${total}` : ""} ·{" "}
              <b style={{ color: "var(--t-bright)" }}>{picked.length} selected</b>
            </span>
            {picked.length > 0 && (
              <button className="btn ghost" onClick={() => setPicked([])}>Clear</button>
            )}
            {total > shown.length && (
              <button className="btn ghost"
                title="select every asset the filter covers, not just this page"
                onClick={async () => {
                  const all = await fleet.candidates(selector, 500);
                  setPicked(all.assets.map((a) => a.asset_id));
                }}>
                Select all {total}
              </button>
            )}
          </div>
          <div className="tblwrap" style={{ maxHeight: 240, overflowY: "auto" }}>
            <table className="tbl">
              {/* STICKY. The list scrolls inside a fixed box, and a header
                  that scrolls away leaves four unlabelled columns - which is
                  exactly when you are reading them to decide what to tick. */}
              <thead style={{ position: "sticky", top: 0, zIndex: 1,
                              background: "var(--surface)" }}><tr>
                <th style={{ width: 30 }}>
                  <input type="checkbox" checked={allShownPicked}
                    title="select all shown (filtered)"
                    onChange={(e) => {
                      const ids = shown.map((a) => a.asset_id);
                      setPicked(e.target.checked
                        ? [...new Set([...picked, ...ids])]
                        : picked.filter((id) => !ids.includes(id)));
                    }} />
                </th>
                <th>Asset</th><th>Company</th><th>Type</th><th>State</th>
                {/* Dattatray: *"the table that we have on commands page for
                    asset shall have the staus of each asset"*. The vehicle's
                    OWN state, which is a different question from `state`
                    above -- that one is the asset's lifecycle (deployed,
                    retired), this one is whether it can move right now. */}
                <th>Vehicle</th><th>Fence held off</th>
              </tr></thead>
              <tbody>
                {shown.length === 0 && (
                  <tr><td colSpan={7} className="cap">
                    {!company
                      ? "Choose one company in the top bar to list its assets."
                      : cands.isLoading
                        ? "…"
                        : "No asset matches, or none has a telematics box fitted."}
                  </td></tr>
                )}
                {shown.map((a) => (
                  <tr key={a.asset_id}>
                    <td>
                      <input type="checkbox" checked={picked.includes(a.asset_id)}
                        onChange={(e) => setPicked(e.target.checked
                          ? [...picked, a.asset_id]
                          : picked.filter((id) => id !== a.asset_id))} />
                    </td>
                    <td className="mono">{a.asset_id}</td>
                    <td>{a.tenant_id}</td>
                    <td>{a.asset_type}</td>
                    <td>{a.state}</td>
                    <td><VehicleCell a={a} /></td>
                    <td><LockoutCell until={a.lockout_until ?? null} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <>
          <div style={{ display: "flex", gap: 12, alignItems: "center",
                        justifyContent: "space-between", flexWrap: "wrap",
                        marginBottom: 8 }}>
            <span style={{ fontSize: 12, color: "var(--t-mute)" }}>
              One asset id per row. Paste below, or load a CSV.
            </span>
            <span style={{ display: "flex", gap: 8 }}>
              <a className="btn ghost sm" href={fleet.bulkTemplateUrl(spec.key)}>
                <Icon name="download" /> Template
              </a>
              <FilePick accept=".csv,text/csv" label="Choose .csv"
                fileName={fileName} onFile={onFile} before />
            </span>
          </div>
          <textarea className="inp" rows={5} value={text}
            placeholder={"AE-2W-TEST-VIN005\nAE-2W-TEST-VIN007"}
            onChange={(e) => setText(e.target.value)} />
          {parseError && (
            <div style={{ fontSize: 11.5, color: "var(--bad)" }}>{parseError}</div>
          )}
          {/* WHAT THE SECOND COLUMN DID, said out loud. A reason that only
              reaches the server is a reason the operator cannot check before
              stopping 200 vehicles, and a cell this screen could not read has
              to refuse rather than round down to the run's code. */}
          {parsed.bad.length > 0 && (
            <div style={{ fontSize: 11.5, color: "var(--bad)", marginTop: 6 }}>
              {parsed.bad.length} row{parsed.bad.length === 1 ? "" : "s"} have
              something other than a reason code 0 to 3 in the second column
              (first: "{parsed.bad[0]}"). One asset id per row; the second
              column is that vehicle's reason code, or blank.
            </div>
          )}
          {parsed.ignored > 0 && (
            <div style={{ fontSize: 11.5, color: "var(--warn)", marginTop: 6 }}>
              {parsed.ignored} row{parsed.ignored === 1 ? "" : "s"} carry a
              reason code, which only a platform operator may set. Remove the
              second column before starting this run.
            </div>
          )}
          <div style={{ fontSize: 11.5, color: "var(--t-mute)", marginTop: 6 }}>
            {pastedIds.length} listed
          </div>
        </>
      )}
      </>)}

      <div style={{ display: "flex", gap: 12, alignItems: "flex-end",
                    justifyContent: "flex-end", flexWrap: "wrap", marginTop: 12 }}>
        <Field label="Run name">
          <SearchInput style={{ width: 220 }} placeholder="e.g. depot 4 arrears"
            value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        {/* FOTA's canary lever, same name, same meaning: how many devices this
            run may touch per tick. Blank = the worker's global budget. */}
        {/* CONFIGURABLE LIFETIME (OFL-06). It lived on the single-asset
            panel; when that panel went, the requirement would have gone with
            it. It bounds how long Orbit keeps RETRYING delivery - not how
            long the restraint lasts, which is why it does not say "expires". */}
        <Field label="Retry delivery for">
          <Select style={{ width: 150 }} value={lifeMin}
            onChange={(e) => setLifeMin(e.target.value)}>
            <option value="">Default</option>
            <option value="5">5 minutes</option>
            <option value="60">1 hour</option>
            <option value="360">6 hours</option>
            <option value="1440">24 hours</option>
            <option value="10080">7 days</option>
          </Select>
        </Field>
        {/* ON A RELEASE ONLY. Offered for every mobilise rather than only
            when the asset is outside a fence: a run covers many assets and
            the console cannot cheaply know which of them are outside, and a
            window on a vehicle that is inside costs nothing because nothing
            would re-stop it. The honest simplification; the gate would need
            a per-asset fence read this screen does not do. */}
        {spec.key === "mobilize" && (
          <Field label="Hold the fence off for">
            <Select style={{ width: 170 }} value={lockMin}
              onChange={(e) => setLockMin(e.target.value)}>
              <option value="">No window</option>
              <option value="15">15 minutes</option>
              <option value="60">1 hour</option>
              <option value="120">2 hours</option>
              <option value="360">6 hours</option>
              <option value="1440">24 hours</option>
            </Select>
          </Field>
        )}
        <Field label="Wave size (per tick)">
          <SearchInput style={{ width: 130 }} placeholder="all"
            value={wave} onChange={(e) => setWave(e.target.value.replace(/\D/g, ""))} />
        </Field>
        {/* BLOCKED BY A ROW THIS SCREEN COULD NOT READ. Creating the run
            anyway would give that vehicle the run's code instead of the one
            in the sheet -- a silent substitution on exactly the rows an
            operator was most deliberate about, and invisible afterwards
            because the stored reason looks like a choice somebody made.
            `ignored` blocks for the same reason in the other direction: the
            codes cannot be sent at all, so the run would record nothing
            where the operator believes they recorded why. */}
        <button className={actionClass(spec, true)}
          title={parsed.bad.length > 0 || parsed.ignored > 0
            ? "fix the reason column in the pasted list first"
            : isFence && fenceKind === "disable"
              ? "Turns off your own level's fence and mobilises any vehicle that fence had stopped, at that level only. A hold from another level, or from another cause at this level, is untouched."
              : isFence
                ? "Arming again replaces your existing fence on these assets."
                : undefined}
          disabled={targetCount === 0 || !mayCommand
            || (mode !== "list"
                && (parsed.bad.length > 0 || parsed.ignored > 0))
            || (isFence ? (!fenceReady || createFence.isPending
                            || disableFence.isPending)
                        : create.isPending)}
          onClick={() => { setFenceOut(null);
            return isFence
              ? (fenceKind === "disable" ? disableFence.mutate() : createFence.mutate())
              : create.mutate(); }}>
          {/* ARM, NOT CREATE. A fence is not a run: nothing is published,
              nothing is acknowledged and there is no start step. Calling it
              "Create run" would promise a batch the operator could then look
              for and not find. */}
          <Icon name={spec.icon} />{" "}
          {isFence
            ? (fenceKind === "disable"
              ? `Disable fence (${targetCount})`
              : `Arm fence (${targetCount})`)
            : `Create run (${targetCount})`}
        </button>
      </div>
      {!mayCommand && (
        <div style={{ fontSize: 11.5, color: "var(--t-mute)", marginTop: 6 }}>
          You can review a selection. Creating a run needs the immobilise
          permission.
        </div>
      )}
      {/* THE OUTCOME, IN WORDS. Amber rather than green whenever anything
          was left undone, because the failure this exists for looked like a
          success. */}
      {fenceOut && (
        <div style={{ fontSize: 11.5, marginTop: 6,
                      color: /had no fence|refused|still held/.test(fenceOut)
                        ? "var(--warn)" : "var(--ok)" }}>
          {fenceOut}
        </div>
      )}
      {(disableFence.isError || createFence.isError) && (
        <div style={{ fontSize: 11.5, color: "var(--bad)", marginTop: 6 }}>
          {String(((disableFence.error ?? createFence.error) as Error)?.message
                  ?? "the fence call failed")}
        </div>
      )}
      {create.isError && (
        <div style={{ fontSize: 11.5, color: "var(--bad)", marginTop: 6 }}>
          {String((create.error as Error)?.message ?? "could not create the run")}
        </div>
      )}
      <div style={{ fontSize: 11.5, color: "var(--t-mute)", marginTop: 6 }}>
        A run is created as a <b>draft</b>. No hold is placed and nothing is
        sent until you press Start.
      </div>
    </div>
  );
}
